"use client"

import { getAuthHeader } from "../utils/auth" // ★ 1. 認証ヘッダー取得関数をインポート
import { useEffect, useState, useCallback } from "react"
import { useOutletContext } from "react-router-dom"

const API_URL = "https://salesbreaker.jp"

// --- Components ---
function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, currentItemCount }) {
  if (totalPages <= 1) return null

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = startItem + currentItemCount - 1

  return (
    <div className="flex flex-col gap-3 mt-4 p-4 border-t sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-gray-700">
        {totalItems > 0
          ? `${totalItems.toLocaleString()} 件中 ${startItem.toLocaleString()} - ${endItem.toLocaleString()} 件を表示`
          : "該当する契約がありません"}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          前へ
        </button>
        <span className="text-sm text-gray-700">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          次へ
        </button>
      </div>
    </div>
  )
}

function EditModal({ isOpen, subscription, onClose, onSave, user }) {
  const [interactions, setInteractions] = useState([])
  const [newInteractionType, setNewInteractionType] = useState("定期連絡")
  const [newNotes, setNewNotes] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [contactInfo, setContactInfo] = useState({ name: "", email: "", phone: "" })

  const fetchInteractions = useCallback(async () => {
    if (!subscription) return
    try {
      const res = await fetch(`${API_URL}/api/admin/interactions/${subscription.client_id}`, {
        headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
      })
      const data = await res.json()
      if (data.success) setInteractions(data.interactions || [])
    } catch (error) {
      console.error("対応履歴の取得エラー:", error)
    }
  }, [subscription])

  useEffect(() => {
    if (isOpen && subscription) {
      fetchInteractions()
      setContactInfo({
        name: subscription.contact_name || "",
        email: subscription.contact_email || "",
        phone: subscription.contact_phone || "",
      })
    }
  }, [isOpen, subscription, fetchInteractions])

  if (!isOpen || !subscription) return null

  const handleInfoChange = (e) => setContactInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSaveContactInfo = async () => {
    if (!window.confirm("顧客の連絡先情報を更新します。よろしいですか？")) return
    setIsLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/users/details/${subscription.user_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        },
        body: JSON.stringify({
          contact_name: contactInfo.name,
          contact_email: contactInfo.email,
          contact_phone: contactInfo.phone,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      alert("顧客情報を更新しました。")
    } catch (error) {
      alert(`エラー: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveInteractionClick = async () => {
    setIsLoading(true)
    try {
      await onSave({
        client_id: subscription.client_id,
        user_id: user.id,
        interaction_type: newInteractionType,
        notes: newNotes,
      })
      setNewNotes("")
      setNewInteractionType("定期連絡")
      fetchInteractions()
    } catch (error) {
      alert(`エラー: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl flex flex-col">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">{subscription.company_name} - 顧客詳細</h3>
        </div>
        <div className="flex-grow p-6 overflow-y-auto" style={{ maxHeight: "70vh" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* 左カラム: 顧客情報 */}
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">顧客情報</h4>
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-xs text-gray-500">担当者名</label>
                    <input
                      type="text"
                      name="name"
                      value={contactInfo.name}
                      onChange={handleInfoChange}
                      className="w-full p-1 border rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">メールアドレス</label>
                    <input
                      type="email"
                      name="email"
                      value={contactInfo.email}
                      onChange={handleInfoChange}
                      className="w-full p-1 border rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">電話番号</label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactInfo.phone}
                      onChange={handleInfoChange}
                      className="w-full p-1 border rounded"
                    />
                  </div>
                  <button
                    onClick={handleSaveContactInfo}
                    disabled={isLoading}
                    className="w-full mt-2 bg-blue-500 text-white text-sm font-semibold py-1 px-3 rounded-md hover:bg-blue-600 disabled:bg-gray-400"
                  >
                    連絡先を更新
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold">新規対応を登録</h4>
                <div className="space-y-2">
                  <select
                    value={newInteractionType}
                    onChange={(e) => setNewInteractionType(e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    <option>定期連絡</option>
                    <option>サポート対応</option>
                    <option>クレーム対応</option>
                    <option>契約更新の確認</option>
                    <option>入金遅延の連絡</option>
                    <option>アップセル提案</option>
                    <option>その他</option>
                  </select>
                  <textarea
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    rows="4"
                    className="w-full p-2 border rounded-md"
                    placeholder="対応内容メモ..."
                  ></textarea>
                  <button
                    onClick={handleSaveInteractionClick}
                    disabled={isLoading}
                    className="w-full bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-500"
                  >
                    {isLoading ? "保存中..." : "対応履歴を登録"}
                  </button>
                </div>
              </div>
            </div>

            {/* 右カラム: 対応履歴 */}
            <div className="space-y-4">
              <h4 className="font-semibold">過去の対応履歴</h4>
              <div className="space-y-3 max-h-96 lg:max-h-[500px] overflow-y-auto pr-2 border-l pl-4">
                {interactions.length > 0 ? (
                  interactions.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-md border">
                      <p className="font-bold text-sm">
                        {item.interaction_type}{" "}
                        <span className="text-xs font-normal text-gray-500">- by {item.staff_name}</span>
                      </p>
                      <p className="text-xs text-gray-500">{new Date(item.interaction_date).toLocaleString("ja-JP")}</p>
                      <p className="mt-2 text-sm whitespace-pre-wrap">{item.notes}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">対応履歴はまだありません。</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">
            閉じる
          </button>
        </div>
      </div>
    </div>
  )
}

function ClientManagementPage() {
  const { user } = useOutletContext()
  const [subscriptions, setSubscriptions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [pagination, setPagination] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("")
  const [riskFilter, setRiskFilter] = useState("")
  const [editStates, setEditStates] = useState({})
  const [editingSubscription, setEditingSubscription] = useState(null)
  const [plans, setPlans] = useState([]); // ★★★ プラン一覧を保持するStateを追加 ★★★

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm])

  const fetchSubscriptions = useCallback(async () => {
    setIsLoading(true)
    setError("")
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        searchTerm: debouncedSearchTerm,
        statusFilter: statusFilter,
        paymentMethodFilter: paymentMethodFilter,
        riskFilter: riskFilter,
      })
      const response = await fetch(`/api/admin/subscriptions?${params.toString()}`, {
        headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
      })
      const data = await response.json()
      if (!data.success) throw new Error(data.error || "データの取得に失敗しました。")
      setSubscriptions(data.subscriptions)
      setPagination(data.pagination)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, debouncedSearchTerm, statusFilter, paymentMethodFilter, riskFilter])

  // ★★★ プラン一覧を取得する関数を追加 ★★★
  const fetchPlans = useCallback(async () => {
    try {
        const res = await fetch(`${API_URL}/api/products`, {
          headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        });
        const data = await res.json();
        if (data.success) {
            setPlans(data.products || []);
        }
    } catch (error) {
        console.error("プラン一覧の取得エラー:", error);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions()
  }, [fetchSubscriptions])

  // ★★★ 初期表示時にプラン一覧も取得 ★★★
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleRiskFilterChange = (e) => {
    setRiskFilter(e.target.value)
    setCurrentPage(1)
  }

  const handlePaymentMethodFilterChange = (e) => {
    setPaymentMethodFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleEditChange = (id, field, value) => {
    setEditStates((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }

  const handleSaveUpdate = async (sub) => {
    const changes = editStates[sub.id] || {}
    const payload = {}
    if (changes.status && changes.status !== sub.status) {
      payload.status = changes.status
    }
    if (changes.current_period_ends_at && changes.current_period_ends_at !== formatDate(sub.current_period_ends_at)) {
      payload.current_period_ends_at = changes.current_period_ends_at
    }
    // ★★★ プラン変更をペイロードに追加 ★★★
    if (changes.plan_id_string && changes.plan_id_string !== sub.plan) {
        payload.plan_id_string = changes.plan_id_string;
    }

    if (Object.keys(payload).length === 0) {
      alert("変更点がありません。")
      return
    }
    try {
      const res = await fetch(`/api/admin/subscriptions/${sub.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      alert("更新しました。")
      fetchSubscriptions()
      setEditStates((prev) => {
        const newStates = { ...prev }
        delete newStates[sub.id]
        return newStates
      })
    } catch (err) {
      alert(`更新エラー: ${err.message}`)
    }
  }

  const handleSaveInteraction = async (interactionData) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/interactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        },
        body: JSON.stringify(interactionData),
      })
      if (!res.ok) throw new Error("履歴の保存に失敗しました。")
    } catch (error) {
      alert("対応履歴の保存に失敗しました。")
      throw error
    }
  }

  const handleModalClose = () => {
    setEditingSubscription(null)
    fetchSubscriptions()
  }

  const formatDate = (dateString) => {
    if (!dateString) return "---"
    try {
      const date = new Date(dateString)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
        date.getDate(),
      ).padStart(2, "0")}`
    } catch (e) {
      return "---"
    }
  }

  // 契約終了・支払い滞納の状態を、行を開かなくても分かるようにする
  // (2026-08-19)。お客さんへは通知メールを出すようにしたので、こちら側も
  // 同じことが一覧で見えていないと、気づけるのが通知を見たときだけになる。
  const getAttentionBadges = (sub) => {
    const badges = []
    const isFreeAlready = String(sub.plan || "").toLowerCase() === "free"
    if (sub.cancel_at_period_end === 1 && !isFreeAlready) {
      const endLabel = formatDate(sub.cancel_at || sub.current_period_ends_at)
      badges.push({
        key: "ending",
        label: endLabel === "---" ? "解約予約あり" : `解約予約 ${endLabel}まで`,
        classes: "bg-orange-100 text-orange-800",
      })
    }
    if (sub.past_due_since) {
      const days = Math.floor((Date.now() - new Date(sub.past_due_since).getTime()) / 86400000)
      badges.push({
        key: "overdue",
        label: Number.isFinite(days) && days >= 0 ? `支払い滞納 ${days}日` : "支払い滞納",
        classes: "bg-red-100 text-red-800",
      })
    }
    return badges
  }

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: "bg-green-100 text-green-800",
      trialing: "bg-blue-100 text-blue-800",
      past_due: "bg-red-100 text-red-800",
      canceled: "bg-gray-100 text-gray-800",
      default: "bg-yellow-100 text-yellow-800",
    }
    const classes = statusClasses[status] || statusClasses.default
    return <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${classes}`}>{status || "不明"}</span>
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-screen-xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">🤝 顧客契約管理 (CRM)</h1>
        <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="会社名、担当者名、連絡先等で検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded-md md:col-span-2"
            />
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full p-2 border rounded-md bg-white"
            >
              <option value="">全てのステータス</option>
              <option value="active">有効</option>
              <option value="trialing">トライアル中</option>
              <option value="past_due">支払い延滞</option>
              <option value="canceled">キャンセル済</option>
            </select>
            <select
              value={riskFilter}
              onChange={handleRiskFilterChange}
              className="w-full p-2 border rounded-md bg-white"
            >
              <option value="">要対応で絞らない</option>
              <option value="attention">要対応すべて</option>
              <option value="ending">終了予定（解約予約あり）</option>
              <option value="overdue">支払いが滞っている</option>
              <option value="ended_recently">直近30日で終了した</option>
            </select>
            <select
              value={paymentMethodFilter}
              onChange={handlePaymentMethodFilterChange}
              className="w-full p-2 border rounded-md bg-white"
            >
              <option value="">全ての契約</option>
              <option value="stripe">Stripe契約</option>
              <option value="manual">手動管理 (Square等)</option>
            </select>
          </div>
        </div>
        {isLoading && <div className="p-8 text-center">読み込み中...</div>}
        {error && <div className="p-8 text-red-500">エラー: {error}</div>}
        {!isLoading && !error && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <div className="divide-y divide-gray-100 md:hidden">
                {subscriptions.map((sub) => {
                  const lastInteractionDate = sub.last_interaction_date ? new Date(sub.last_interaction_date) : null
                  const oneMonthAgo = new Date()
                  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
                  const needsFollowUp = lastInteractionDate ? lastInteractionDate < oneMonthAgo : true
                  return (
                    <div key={`${sub.id}-mobile`} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 break-words">{sub.company_name || `(User ID: ${sub.user_id})`}</p>
                          <p className="mt-1 text-xs text-gray-500 break-all">{sub.contact_email}</p>
                          <p className="mt-1 text-xs text-gray-500">{sub.contact_name || '担当者名なし'} / {sub.contact_phone || '電話なし'}</p>
                        </div>
                        <button onClick={() => setEditingSubscription(sub)} className="flex-shrink-0 rounded-md bg-blue-500 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-600">詳細</button>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        {sub.stripe_subscription_id ? (
                          <span className="rounded-full bg-gray-100 px-2 py-1 font-semibold text-gray-700">{sub.plan}</span>
                        ) : (
                          <span className="rounded-full bg-orange-50 px-2 py-1 font-semibold text-orange-700">手動</span>
                        )}
                        {getStatusBadge(sub.status)}
                        {getAttentionBadges(sub).map((badge) => (
                          <span key={badge.key} className={`rounded-full px-2 py-1 font-semibold ${badge.classes}`}>{badge.label}</span>
                        ))}
                        {needsFollowUp && <span className="rounded-full bg-red-100 px-2 py-1 font-bold text-red-700">要連絡</span>}
                      </div>
                      {!sub.stripe_subscription_id && (
                        <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
                          <select value={editStates[sub.id]?.plan_id_string || sub.plan} onChange={(e) => handleEditChange(sub.id, "plan_id_string", e.target.value)} className="w-full rounded-md border bg-white p-2">
                            {plans.map(p => <option key={p.plan_id_string} value={p.plan_id_string}>{p.name}</option>)}
                          </select>
                          <select value={editStates[sub.id]?.status || sub.status} onChange={(e) => handleEditChange(sub.id, "status", e.target.value)} className="w-full rounded-md border bg-white p-2">
                            <option value="active">有効</option>
                            <option value="canceled">キャンセル済</option>
                          </select>
                          <input type="date" value={editStates[sub.id]?.current_period_ends_at || formatDate(sub.current_period_ends_at)} onChange={(e) => handleEditChange(sub.id, "current_period_ends_at", e.target.value)} className="w-full rounded-md border p-2" />
                        </div>
                      )}
                      <div className="mt-3 space-y-1 text-xs text-gray-500">
                        <p>
                          {sub.stripe_subscription_id ? (
                            <>
                              {sub.status === "trialing" ? `トライアル終了: ${formatDate(sub.trial_ends_at)}` : ""}
                              {sub.status === "active" || sub.status === "past_due" ? `請求日: ${formatDate(sub.current_period_ends_at)}` : ""}
                              {sub.status === "canceled" ? `キャンセル日: ${formatDate(sub.canceled_at)}` : ""}
                            </>
                          ) : (
                            `終了日: ${editStates[sub.id]?.current_period_ends_at || formatDate(sub.current_period_ends_at)}`
                          )}
                        </p>
                        <p>最終連絡日: {lastInteractionDate ? formatDate(lastInteractionDate) : "履歴なし"}</p>
                      </div>
                      {!sub.stripe_subscription_id && (
                        <button onClick={() => handleSaveUpdate(sub)} className="mt-4 w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">保存</button>
                      )}
                    </div>
                  )
                })}
              </div>
              <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="py-3 px-4">操作</th>
                    <th className="py-3 px-4">クライアント名</th>
                    <th className="py-3 px-4">主要連絡先</th>
                    <th className="py-3 px-4">プラン</th>
                    <th className="py-3 px-4">ステータス</th>
                    <th className="py-3 px-4">次回請求/終了日</th>
                    <th className="py-3 px-4">最終連絡日</th>
                    <th className="py-3 px-4">管理</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {subscriptions.map((sub) => {
                    const lastInteractionDate = sub.last_interaction_date ? new Date(sub.last_interaction_date) : null
                    const oneMonthAgo = new Date()
                    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
                    const needsFollowUp = lastInteractionDate ? lastInteractionDate < oneMonthAgo : true

                    return (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setEditingSubscription(sub)}
                            className="px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-md hover:bg-blue-600"
                          >
                            詳細
                          </button>
                        </td>
                        <td className="py-3 px-4 font-medium">{sub.company_name || `(User ID: ${sub.user_id})`}</td>
                        <td className="py-3 px-4">
                          <div>{sub.contact_name}</div>
                          <div className="text-xs text-gray-500">{sub.contact_email}</div>
                          <div className="text-xs text-gray-500">{sub.contact_phone}</div>
                        </td>
                        <td className="py-3 px-4 capitalize">
                          {/* ▼▼▼ ここからが修正箇所 ▼▼▼ */}
                          {sub.stripe_subscription_id ? (
                            sub.plan
                          ) : (
                            <select
                                value={editStates[sub.id]?.plan_id_string || sub.plan}
                                onChange={(e) => handleEditChange(sub.id, "plan_id_string", e.target.value)}
                                className="p-1 border rounded-md bg-white text-xs"
                            >
                                {plans.map(p => <option key={p.plan_id_string} value={p.plan_id_string}>{p.name}</option>)}
                            </select>
                          )}
                          {!sub.stripe_subscription_id && <span className="ml-1 text-xs text-orange-600">(手動)</span>}
                          {/* ▲▲▲ ここまでが修正箇所 ▲▲▲ */}
                        </td>
                        <td className="py-3 px-4">
                          {sub.stripe_subscription_id ? (
                            getStatusBadge(sub.status)
                          ) : (
                            <select
                              value={editStates[sub.id]?.status || sub.status}
                              onChange={(e) => handleEditChange(sub.id, "status", e.target.value)}
                              className="p-1 border rounded-md bg-white text-xs"
                            >
                              <option value="active">有効</option>
                              <option value="canceled">キャンセル済</option>
                            </select>
                          )}
                          {getAttentionBadges(sub).map((badge) => (
                            <div key={badge.key} className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${badge.classes}`}>{badge.label}</div>
                          ))}
                        </td>
                        <td className="py-3 px-4">
                          {sub.stripe_subscription_id ? (
                            <>
                              {sub.status === "trialing" ? `トライアル終了: ${formatDate(sub.trial_ends_at)}` : ""}
                              {sub.status === "active" || sub.status === "past_due"
                                ? `請求日: ${formatDate(sub.current_period_ends_at)}`
                                : ""}
                              {sub.status === "canceled" ? `キャンセル日: ${formatDate(sub.canceled_at)}` : ""}
                            </>
                          ) : (
                            <input
                              type="date"
                              value={
                                editStates[sub.id]?.current_period_ends_at || formatDate(sub.current_period_ends_at)
                              }
                              onChange={(e) => handleEditChange(sub.id, "current_period_ends_at", e.target.value)}
                              className="p-1 border rounded-md text-xs"
                            />
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {lastInteractionDate ? formatDate(lastInteractionDate) : "履歴なし"}
                          {needsFollowUp && <span className="ml-2 text-xs text-red-500 font-bold">要連絡!</span>}
                        </td>
                        <td className="py-3 px-4 flex items-center gap-2">
                          {!sub.stripe_subscription_id && (
                            <button
                              onClick={() => handleSaveUpdate(sub)}
                              className="px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                              保存
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={pagination ? pagination.currentPage : 1}
              totalPages={pagination ? pagination.totalPages : 1}
              onPageChange={handlePageChange}
              totalItems={pagination ? pagination.totalItems : 0}
              itemsPerPage={pagination ? pagination.itemsPerPage : 50}
              currentItemCount={subscriptions.length}
            />
          </div>
        )}
      </div>
      <EditModal
        isOpen={!!editingSubscription}
        subscription={editingSubscription}
        user={user}
        onClose={handleModalClose}
        onSave={handleSaveInteraction}
      />
    </div>
  )
}

export default ClientManagementPage
